import { ref, readonly } from 'vue';
import { transferProjectOwnershipApi } from '@/apis/projects';
import { useUserLogin } from '@eureka/shared/src/login-sdk/src/composables/useUserLogin';

// Global migration state
const isMigrationInProgress = ref(false);
const migrationPromise = ref<Promise<void> | null>(null);

/**
 * 检查用户是否有权限执行迁移
 */
const isUserAuthorized = (): boolean => {
    try {
        const { isLoggedIn } = useUserLogin();
        // 只需要满足登录状态即可
        if (!isLoggedIn.value) {
            return false;
        }
        return true;
    } catch (error) {
        console.warn('[MigrationGuard] Failed to get user login data:', error);
        return false;
    }
};

/**
 * 尝试迁移单个项目
 */
const tryMigrateProject = async (projectId: string): Promise<boolean> => {
    try {
        console.log(`[MigrationGuard] Attempting to migrate project: ${projectId}`);
        await transferProjectOwnershipApi(projectId);
        console.log(`[MigrationGuard] Successfully migrated project: ${projectId}`);
        return true;
    } catch (error) {
        console.error(`[MigrationGuard] Failed to migrate project ${projectId}:`, error);
        return false;
    }
};

/**
 * 执行迁移逻辑 - 导出到模块级别供路由守卫使用
 *
 * 这个函数会在路由守卫中被调用，确保所有页面加载时都会检测和执行迁移
 */
export const executeMigration = async (): Promise<void> => {
    console.log('[MigrationGuard] executeMigration called from router guard');

    // 如果已经在迁移中，返回现有的Promise
    if (isMigrationInProgress.value && migrationPromise.value) {
        console.log('[MigrationGuard] Migration already in progress, waiting...');
        return migrationPromise.value;
    }

    // 检查localStorage中是否有guest_project_ids
    const guestProjectIdsStr = localStorage.getItem('guest_project_ids');
    if (!guestProjectIdsStr) {
        console.log('[MigrationGuard] No guest_project_ids found in localStorage');
        return;
    }

    // 检查用户权限
    if (!isUserAuthorized()) {
        console.log('[MigrationGuard] User not authorized for migration (not logged in), keeping guest_project_ids for later');
        return;
    }

    // 解析项目ID列表
    let projectIds: string[];
    try {
        projectIds = JSON.parse(guestProjectIdsStr);
        if (!Array.isArray(projectIds) || projectIds.length === 0) {
            console.log('[MigrationGuard] guest_project_ids is empty or invalid');
            localStorage.removeItem('guest_project_ids');
            return;
        }
    } catch (error) {
        console.error('[MigrationGuard] Failed to parse guest_project_ids:', error);
        localStorage.removeItem('guest_project_ids');
        return;
    }

    console.log(`[MigrationGuard] Starting migration for ${projectIds.length} projects:`, projectIds);

    // 设置迁移状态
    isMigrationInProgress.value = true;

    // 创建迁移Promise
    migrationPromise.value = (async () => {
        try {
            // 并行处理所有项目迁移
            const migrationResults = await Promise.allSettled(
                projectIds.map(projectId => tryMigrateProject(projectId))
            );

            // 统计迁移结果
            const successCount = migrationResults.filter(
                result => result.status === 'fulfilled' && result.value === true
            ).length;

            console.log(`[MigrationGuard] Migration completed: ${successCount}/${projectIds.length} projects migrated successfully`);

            // 清理localStorage中的guest_project_ids
            localStorage.removeItem('guest_project_ids');
            console.log('[MigrationGuard] Cleaned up guest_project_ids from localStorage');

        } catch (error) {
            console.error('[MigrationGuard] Migration process failed:', error);
        } finally {
            // 重置迁移状态
            isMigrationInProgress.value = false;
            migrationPromise.value = null;
        }
    })();

    return migrationPromise.value;
};
/**
 * 项目迁移守卫 composable
 *
 * 提供迁移状态查询和等待功能，主要用于向后兼容
 * 实际的迁移逻辑现在在路由层面统一处理
 */
export function useMigrationGuard() {
    /**
     * 等待迁移完成
     *
     * 这个函数现在主要用于确保API调用在迁移完成后执行
     * 但由于迁移现在在路由层面处理，大多数情况下迁移已经完成
     */
    const waitForMigration = async (): Promise<void> => {
        if (isMigrationInProgress.value && migrationPromise.value) {
            console.log('[MigrationGuard] Waiting for ongoing migration to complete...');
            await migrationPromise.value;
        }
    };

    return {
        executeMigration,
        waitForMigration,
        isMigrationInProgress: readonly(isMigrationInProgress),
    };
}